/**
 * Floor Plan Detail Plugin for Native Interactive Site Map
 *
 * Owns [data-panel] content when activeView === VIEW_FLOOR_PLAN_DETAIL: the sticky header, the
 * Lot & Elevation / Floor Plan Configuration tabs (components/tabs.dom.js) and their content
 * (components/lotAndElevationFloorPlan.dom.js, components/floorPlanConfiguration.dom.js), and the floor
 * plan's layout image — shown desktop-only in [data-secondary-panel] (an overlay over the map,
 * same footprint, never replacing it) while the Configuration tab is active, or behind a "View
 * Floor Plan" button on mobile that expands [data-panel] itself to a full-screen image + Back
 * screen (components/floorPlanViewer.dom.js), since there's no secondary panel there. Also owns
 * floor plan card clicks within the panel (data-plan-id elements) — they dispatch
 * floorPlanCardSelected, which updates activeViewContext.planId in place. Registers
 * floorPlanCardSelectedEpic (turns card clicks into navigation actions) via registerEpicOnce.
 *
 * `isImageExpanded` (the mobile full-screen state) is local to this plugin, not Redux — no other
 * part of the tree needs it. Its Back control only collapses this local state, it does not
 * dispatch navigateBack() / touch viewHistory — the header's own Back button is the one that
 * does that, leaving this view entirely.
 *
 * Leaving this view has no dedicated control here, same as lotMapSelection: tapping the map
 * outside any lot dispatches navigateBack() from lotInteraction.plugin.js, which is
 * view-agnostic and already handles this view correctly.
 *
 * @module nativeInteractiveSiteMap/plugins/floorPlanDetail.plugin
 */

import { distinctUntilChanged, map, filter } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import createFloorPlanDetailHeader from '../components/floorPlanDetailHeader.dom';
import createTabsDom from '../components/tabs.dom';
import createLotAndElevationDom from '../components/lotAndElevationFloorPlan.dom';
import createFloorPlanConfigurationDom from '../components/floorPlanConfiguration.dom';
import createFloorPlanDetailFooterDom from '../components/floorPlanDetailFooter.dom';
import createSecondaryPanelController from '../components/secondaryPanelController.dom';
import createFloorPlanViewer from '../components/floorPlanViewer.dom';
import renderViewFloorPlanButton from '../components/viewFloorPlanButton.dom';
import createTabInteractionHandlers from '../handlers/tabInteractionHandlers';
import {
  selectActiveView,
  selectActiveFloorPlan,
  selectCurrentlySelectedLot,
  selectSearchParameters,
} from '../redux/selectors';
import createFloorPlanCardSelectedEpic from '../redux/floorPlanCardSelectedEpic';
import registerEpicOnce from '../redux/registerEpicOnce';
import LotStatus from '../enums/lotStatus';
import { deriveActiveChip } from '../services/mapChipService';
import {
  VIEW_FLOOR_PLAN_DETAIL,
  TAB_LOT_AND_ELEVATION,
  TAB_FLOOR_PLAN_CONFIG,
} from '../constants';

/**
 * Chips whose cards are allowed to navigate here — the same statuses
 * services/lotRoutingService.js#resolveLotDestination treats as eligible for a lot click, so a
 * Model home's card (rendered via the floor-plan card component inside
 * lotMapSelection.plugin.js's mobile preview, when the lot has housePlanIds but no
 * housingUnitId) can now reach floorPlanDetail the same way an Available floor-plan card already
 * could — see NHS20-8823.
 */
const CARD_CLICK_ELIGIBLE_CHIPS = [
  LotStatus.Available.toString(),
  LotStatus.Model.toString(),
  LotStatus.Sold.toString()];

export default function createFloorPlanDetailPlugin() {
  const state = {
    header: null,
    panel: null,
    tabs: null,
    selectedTab: TAB_LOT_AND_ELEVATION,
    lotAndElevationContent: null,
    configContent: null,
    footer: null,
    secondaryPanelController: null,
    mapContainer: null,
    floorPlanViewer: null,
    isMobile: false,
    isImageExpanded: false,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    cardClickSubscription: null,
    subscriptions: [],
  };

  function setupCardClickListener() {
    const cardSelector = '[data-plan-id]';

    state.cardClickSubscription = fromEvent(state.panel, 'click')
      .pipe(
        map((event) => event.target?.closest?.(cardSelector) ?? null),
        // Only respond to clicks on cards if the active chip is one that can actually navigate
        // here (Available floor-plan card, or a Model home card — see CARD_CLICK_ELIGIBLE_CHIPS
        // above)
        filter(() => CARD_CLICK_ELIGIBLE_CHIPS.includes(deriveActiveChip(selectSearchParameters(state.getState())))),
        filter((cardElement) => cardElement !== null),
        map((cardElement) => parseInt(cardElement.getAttribute('data-plan-id'), 10)),
        filter((planId) => Number.isFinite(planId)),
      )
      .subscribe((planId) => state.dispatch(state.dataActions.floorPlanCardSelected(planId)));
  }

  function teardownCardClickListener() {
    if (state.cardClickSubscription) {
      state.cardClickSubscription.unsubscribe();
      state.cardClickSubscription = null;
    }
  }

  // Creates the secondary panel only while the Configuration tab is active, destroying it the
  // rest of the time (including while on the Lot & Elevation tab, or once this view is left — see
  // remove() below) rather than just hiding it — see secondaryPanelController.dom.js for why.
  // Mobile has no secondary panel; the same image is reached through the "View Floor Plan" button
  // instead (see setImageExpanded below).
  function syncSecondaryPanel() {
    if (state.isMobile) {
      return;
    }

    if (state.selectedTab !== TAB_FLOOR_PLAN_CONFIG) {
      state.secondaryPanelController?.remove();
      state.secondaryPanelController = null;
      return;
    }

    if (!state.mapContainer) {
      return;
    }

    if (!state.secondaryPanelController) {
      state.secondaryPanelController = createSecondaryPanelController(state.mapContainer);
    }

    const activeDetailItem = selectActiveFloorPlan(state.getState());
    state.secondaryPanelController.show({ floorplans: activeDetailItem?.floorplans });
  }

  function syncFooter() {
    const shouldShowFooter = state.selectedTab === TAB_LOT_AND_ELEVATION && !state.isImageExpanded;
    const activeDetailItem = selectActiveFloorPlan(state.getState());

    if (!shouldShowFooter || !activeDetailItem) {
      state.footer?.remove();
      state.footer = null;
      return;
    }

    state.footer?.remove();
    state.footer = createFloorPlanDetailFooterDom(activeDetailItem);
    state.panel.appendChild(state.footer.getElement());
  }

  function switchTab(tabValue) {
    state.selectedTab = tabValue;
    state.tabs?.setActiveTab(tabValue);

    // Remove current content from panel
    state.lotAndElevationContent?.getElement().remove();
    state.configContent?.getElement().remove();

    // Append the appropriate content component
    if (tabValue === TAB_LOT_AND_ELEVATION) {
      state.panel.appendChild(state.lotAndElevationContent.getElement());
    } else if (tabValue === TAB_FLOOR_PLAN_CONFIG) {
      state.panel.appendChild(state.configContent.getElement());
    }

    syncFooter();
    syncSecondaryPanel();
  }

  // When the active plan has no floor plan images there's nothing to switch to, so per design
  // (NHS20-8122) both tabs are hidden entirely rather than just disabling the Configuration one —
  // the Lot & Elevation content is shown directly, with no tab bar above it.
  function syncFloorPlanConfigTabState() {
    if (!state.tabs) {
      return;
    }
    const activeDetailItem = selectActiveFloorPlan(state.getState());
    const hasFloorPlanConfigurations = Array.isArray(activeDetailItem?.floorplans) &&
          activeDetailItem.floorplans.length > 0;

    if (hasFloorPlanConfigurations) {
      if (!state.isImageExpanded) {
        state.tabs.show();
      }
    } else {
      state.tabs.hide();
    }

    // Hiding the tabs while the Configuration one is active (e.g. the user switches to a plan
    // with no floorplans while already on it) would otherwise leave the user stranded on a tab
    // that no longer has a way back — fall back to Lot & Elevation instead.
    if (!hasFloorPlanConfigurations && state.selectedTab === TAB_FLOOR_PLAN_CONFIG) {
      switchTab(TAB_LOT_AND_ELEVATION);
    }
  }

  // Mobile-only full-screen swap: hides the header/tabs/current tab content and shows
  // floorPlanViewer instead, since [data-panel] is already the full-screen drawer there (see
  // DRAWER_PLUGIN_SEQUENCE.md's FULL_VIEWS) — there's no secondary panel to show the image in
  // instead. Only ever reachable from the Configuration tab (the "View Floor Plan" button lives
  // in configContent), so only configContent's visibility needs to be toggled here.
  function setImageExpanded(expanded) {
    if (expanded === state.isImageExpanded) {
      return;
    }
    state.isImageExpanded = expanded;

    if (expanded) {
      state.header.hide();
      state.tabs.hide();
      state.configContent.hide();
      syncFooter();
      state.panel.appendChild(state.floorPlanViewer.getElement());
      state.floorPlanViewer.setImage(selectActiveFloorPlan(state.getState()));
    } else {
      state.header.show();
      syncFloorPlanConfigTabState();
      state.floorPlanViewer.getElement().remove();
      state.configContent.show();
      syncFooter();
    }
  }

  // Only present (see initializeContent()) when state.isMobile — no separate mobile/desktop
  // branch needed here, the button simply won't exist in the DOM on desktop for this to match.
  function setupExpandButtonClickListener() {
    state.subscriptions.push(
      fromEvent(state.panel, 'click')
        .pipe(filter((event) => event.target.closest('[data-image-expand-button]') !== null))
        .subscribe(() => setImageExpanded(true)),
    );
  }

  function initializeContent() {
    // Create content components (cached for reuse)
    const activeDetailItem = selectActiveFloorPlan(state.getState());
    const lot = selectCurrentlySelectedLot(state.getState());
    state.lotAndElevationContent = createLotAndElevationDom(activeDetailItem, lot);
    state.configContent = createFloorPlanConfigurationDom();

    if (state.isMobile) {
      state.configContent.getEmptyStateElement()
        .insertAdjacentHTML('beforeend', renderViewFloorPlanButton());
    }
  }

  function createTabs() {
    state.tabs = createTabsDom([
      {
        label: 'Lot & elevation',
        count: null,
        tabValue: TAB_LOT_AND_ELEVATION,
        isActive: true,
      },
      {
        label: 'Floor plan',
        count: null,
        tabValue: TAB_FLOOR_PLAN_CONFIG,
        isActive: false,
      },
    ]);

    state.tabs.attachEventListeners(
      createTabInteractionHandlers((tabValue) => {
        switchTab(tabValue);
      })
    );

    // Insert tabs before the active content (same pattern as listView)
    const activeContent = state.selectedTab === TAB_LOT_AND_ELEVATION
      ? state.lotAndElevationContent
      : state.configContent;
    state.panel.insertBefore(state.tabs.getElement(), activeContent.getElement());
    syncFloorPlanConfigTabState();
  }

  function updateHeaderContent() {
    const activeDetailItem = selectActiveFloorPlan(state.getState());
    if (state.header && activeDetailItem) {
      state.header.setTitle(activeDetailItem.name);
      state.header.setDetails({
        bedrooms: activeDetailItem.bedrooms,
        bathrooms: activeDetailItem.formattedBathrooms,
        squareFeet: activeDetailItem.formattedSquareFeet,
      });
    }
  }

  function mount() {
    state.header = createFloorPlanDetailHeader();
    state.panel.appendChild(state.header.getElement());
    state.header.onBackClick(() => {
      state.dispatch(state.dataActions.navigateBack());
    });
    updateHeaderContent();

    initializeContent();
    state.panel.appendChild(state.lotAndElevationContent.getElement());
    createTabs();
    syncFooter();
    syncSecondaryPanel();
  }

  function remove() {
    state.header?.remove();
    state.header = null;

    state.lotAndElevationContent?.remove();
    state.lotAndElevationContent = null;

    state.configContent?.remove();
    state.configContent = null;

    state.tabs?.remove();
    state.tabs = null;

    state.footer?.remove();
    state.footer = null;

    state.floorPlanViewer?.getElement().remove();
    state.secondaryPanelController?.remove();
    state.secondaryPanelController = null;

    state.selectedTab = TAB_LOT_AND_ELEVATION;
    state.isImageExpanded = false;
  }

  function onActiveViewChanged(activeView) {
    if (activeView === VIEW_FLOOR_PLAN_DETAIL) {
      mount();
    } else {
      remove();
    }
  }

  return {
    name: 'floor-plan-detail',

    async init(context) {
      const floorPlanCardSelectedEpic = createFloorPlanCardSelectedEpic({
        dataActions: context.services.dataActions,
        store$: context.services.store$,
      });
      registerEpicOnce(context.services.epic$, 'floor-plan-card-selected', floorPlanCardSelectedEpic);
    },

    async start(context) {
      state.panel = context.root.querySelector('[data-panel]');

      if (!state.panel) {
        return;
      }

      state.isMobile = context.state.isMobile;

      // Only ever created on desktop — the secondary panel doesn't exist at all on mobile, since
      // it's never shown there (see syncSecondaryPanel()). Created/destroyed per syncSecondaryPanel(),
      // not eagerly here — see secondaryPanelController.dom.js for why.
      state.mapContainer = context.root.querySelector('[data-map-container]');
      state.floorPlanViewer = createFloorPlanViewer({ onBackClick: () => setImageExpanded(false) });

      state.store$ = context.services.store$;
      state.getState = context.services.getState;
      state.dispatch = context.services.dispatch;
      state.dataActions = context.services.dataActions;

      setupCardClickListener();
      setupExpandButtonClickListener();

      state.subscriptions.push(
        state.store$.pipe(
          map(selectActiveView),
          distinctUntilChanged(),
        ).subscribe(onActiveViewChanged),
      );

      state.subscriptions.push(
        state.store$.pipe(
          filter((currentState) => selectActiveView(currentState) === VIEW_FLOOR_PLAN_DETAIL),
          map(selectActiveFloorPlan),
          distinctUntilChanged(),
        ).subscribe(() => {
          updateHeaderContent();
          syncFooter();
          syncSecondaryPanel();
          syncFloorPlanConfigTabState();
        }),
      );
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      teardownCardClickListener();
      remove();
      state.floorPlanViewer?.remove();
      state.floorPlanViewer = null;
      state.panel = null;
      state.mapContainer = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
      state.header = null;
    },
  };
}
